import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { DayAheadData, IntradayData } from '../types/price';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface PriceComparisonChartProps {
  dayAheadData?: DayAheadData;
  intradayData?: IntradayData;
  title: string;
  isBrutto?: boolean;
  convertPrice?: (price: number) => number;
  showCurrentTime?: boolean;
}

const PriceComparisonChart: React.FC<PriceComparisonChartProps> = ({
  dayAheadData,
  intradayData,
  title,
  isBrutto = false,
  convertPrice = (price) => price,
  showCurrentTime = false,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString);
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
          },
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 18,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
          },
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += (context.parsed.y / 10).toLocaleString('de-DE', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              }) + ' ct/kWh';
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        adapters: {
          date: {
            locale: de,
          },
        },
        time: {
          displayFormats: {
            hour: 'HH:mm',
            day: 'dd.MM',
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 12,
          },
          callback: function(value) {
            return ((value as number) / 10).toLocaleString('de-DE', { 
              minimumFractionDigits: 1, 
              maximumFractionDigits: 1 
            }) + ' ct';
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0, // Disable bezier curves
        borderWidth: 3,
      },
      point: {
        radius: 0,
        hoverRadius: 6,
        hitRadius: 30,
        hoverBorderWidth: 2,
      },
    },
  };

  const datasets = [];

  // Collect all prices to find min/max for current time line
  const allPrices = [];
  if (dayAheadData?.prices) {
    allPrices.push(...dayAheadData.prices.map(p => convertPrice(p.price)));
  }
  if (intradayData?.prices) {
    allPrices.push(...intradayData.prices.map(p => convertPrice(p.price)));
  }
  const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
  const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 100;

  if (dayAheadData?.prices) {
    datasets.push({
      label: 'Day-Ahead Preise (Stündlich)',
      data: dayAheadData.prices.map(p => ({
        x: formatDate(p.timestamp),
        y: convertPrice(p.price),
      })),
      borderColor: 'rgb(53, 162, 235)',
      backgroundColor: 'rgba(53, 162, 235, 0.1)',
      borderWidth: 3,
    });
  }

  if (intradayData?.prices) {
    datasets.push({
      label: 'Intraday Preise (15-Minuten)',
      stepped: true,
      data: intradayData.prices.map(p => ({
        x: formatDate(p.timestamp),
        y: convertPrice(p.price),
      })),
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'rgba(255, 99, 132, 0.1)',
      borderWidth: 2,
    });
  }

  // Add current time line if showing current time
  if (showCurrentTime && allPrices.length > 0) {
    const now = new Date();
    datasets.push({
      label: 'Aktuelle Zeit',
      data: [
        { x: now, y: minPrice },
        { x: now, y: maxPrice }
      ],
      borderColor: 'rgba(255, 69, 0, 0.8)',
      backgroundColor: 'rgba(255, 69, 0, 0.1)',
      borderWidth: 2,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      showLine: true,
    });
  }

  const data = {
    datasets,
  };

  return (
    <div className="price-chart-container">
      <Line options={options} data={data} />
    </div>
  );
};

export default PriceComparisonChart;